'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import SignUpForm from './SignUpForm';
import AppLogo from '@/components/ui/AppLogo';
import { Building2, ClipboardList, Users, BarChart3 } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


export default function AuthPage() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen flex bg-background">
      {/* Brand panel */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] bg-primary flex-col justify-between p-10 xl:p-14 relative overflow-hidden">
        {/* Background decorative shapes */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-white/5 translate-y-1/2 -translate-x-1/2" />

        {/* Logo */}
        <div className="flex items-center gap-3 relative z-10">
          <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
            <AppLogo size={28} />
          </div>
          <span className="text-xl font-800 text-white tracking-tight">CampusMaint</span>
        </div>

        {/* Hero copy */}
        <div className="relative z-10 space-y-6">
          <h2 className="text-hero-xl font-800 text-white leading-tight">
            Manutenção predial<br />
            <span className="text-amber-300">inteligente</span> para<br />
            seu campus
          </h2>
          <p className="text-base text-white/70 leading-relaxed max-w-md">
            Gerencie ordens de serviço, equipes técnicas, estoques e indicadores
            de desempenho em uma plataforma unificada — do chamado ao encerramento.
          </p>

          {/* Feature highlights */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: ClipboardList, label: 'Ordens de Serviço', sub: 'Ciclo completo' },
              { icon: Users,         label: 'Equipes Técnicas',  sub: 'Disponibilidade em tempo real' },
              { icon: Building2,     label: 'Prédios e Ativos',  sub: 'Histórico de manutenções' },
              { icon: BarChart3,     label: 'KPIs e Relatórios', sub: 'SLA, custos e desempenho' },
            ]?.map((feat) => {
              const Icon = feat?.icon;
              return (
                <div key={`feat-${feat?.label}`} className="flex items-start gap-3 bg-white/10 rounded-xl p-3">
                  <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                    <Icon size={16} className="text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-600 text-white leading-tight">{feat?.label}</p>
                    <p className="text-xs text-white/60 mt-0.5">{feat?.sub}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom stat row */}
        <div className="relative z-10 flex items-center gap-8 border-t border-white/20 pt-6">
          {[
            { value: '1.200+', label: 'OS por mês' },
            { value: '48',     label: 'Técnicos ativos' },
            { value: '94%',    label: 'SLA médio' },
          ]?.map((stat) => (
            <div key={`stat-${stat?.label}`}>
              <p className="text-xl font-800 text-white tabular-nums">{stat?.value}</p>
              <p className="text-xs text-white/60 mt-0.5">{stat?.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-10">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={32} />
            <span className="font-800 text-lg text-foreground">CampusMaint</span>
          </div>

          {/* Tab switcher */}
          <div className="flex bg-secondary rounded-xl p-1 mb-8">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2.5 text-sm font-600 rounded-lg transition-all duration-200 ${
                mode === 'login' ?'bg-card text-foreground shadow-card' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Entrar
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2.5 text-sm font-600 rounded-lg transition-all duration-200 ${
                mode === 'signup' ?'bg-card text-foreground shadow-card' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Criar conta
            </button>
          </div>

          {mode === 'login' ? <LoginForm /> : <SignUpForm />}
        </div>
      </div>
    </div>
  );
}